import { useState, useEffect } from 'react'
import { X, History, Search, Calendar, FileText, Image as ImageIcon, Loader2 } from 'lucide-react'
import { analyzerApi, ImageAnalysis, API_BASE } from '@/services/api'

interface AnalysisHistoryModalProps {
  onClose: () => void
  onSelect: (analysis: ImageAnalysis) => void
}

type SortOption = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc'

// Helper function to format date as relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return '방금 전'
  if (diffMins < 60) return `${diffMins}분 전`
  if (diffHours < 24) return `${diffHours}시간 전`
  if (diffDays < 30) return `${diffDays}일 전`

  return date.toLocaleDateString('ko-KR')
}

// Helper function to get display name for analysis
function getAnalysisName(analysis: ImageAnalysis): string {
  if (analysis.asset_id) {
    return `Asset ${analysis.asset_id.slice(0, 12)}`
  }
  return `분석 ${analysis.id.slice(0, 12)}`
}

export function AnalysisHistoryModal({ onClose, onSelect }: AnalysisHistoryModalProps) {
  const [analyses, setAnalyses] = useState<ImageAnalysis[]>([])
  const [filteredAnalyses, setFilteredAnalyses] = useState<ImageAnalysis[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('date-desc')

  useEffect(() => {
    loadAnalyses()
  }, [])

  useEffect(() => {
    filterAndSort()
  }, [analyses, searchQuery, sortBy])

  const loadAnalyses = async () => {
    setIsLoading(true)
    try {
      const result = await analyzerApi.listAnalyses({ limit: 100 })
      if (result.success && result.data) {
        setAnalyses(result.data)
      }
    } catch (err) {
      console.error('Failed to load analyses:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const filterAndSort = () => {
    let filtered = [...analyses]

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(analysis => {
        const displayName = getAnalysisName(analysis).toLowerCase()
        const analysisId = analysis.id.toLowerCase()
        const assetId = (analysis.asset_id || '').toLowerCase()
        return displayName.includes(query) || analysisId.includes(query) || assetId.includes(query)
      })
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        case 'date-asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        case 'name-asc':
          return getAnalysisName(a).localeCompare(getAnalysisName(b))
        case 'name-desc':
          return getAnalysisName(b).localeCompare(getAnalysisName(a))
        default:
          return 0
      }
    })

    setFilteredAnalyses(filtered)
  }

  const handleSelect = (analysis: ImageAnalysis) => {
    onSelect(analysis)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-bg-primary border-b border-border-subtle p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <History className="w-6 h-6 text-accent-gold" />
            <div>
              <h2 className="text-xl font-bold">분석 이력</h2>
              <p className="text-sm text-text-tertiary">
                최근 분석한 이미지를 선택하세요
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search and Sort */}
        <div className="p-4 border-b border-border-subtle bg-bg-secondary">
          <div className="flex gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="이름 또는 ID로 검색..."
                className="input pl-10 w-full"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="input w-48"
            >
              <option value="date-desc">최신순</option>
              <option value="date-asc">오래된순</option>
              <option value="name-asc">이름 (가나다순)</option>
              <option value="name-desc">이름 (가나다역순)</option>
            </select>
          </div>

          {/* Results count */}
          <p className="text-xs text-text-tertiary mt-2">
            {filteredAnalyses.length}개의 분석 결과
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent-gold mb-3" />
              <p className="text-text-secondary">분석 이력을 불러오는 중...</p>
            </div>
          ) : filteredAnalyses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <History className="w-12 h-12 text-text-tertiary mb-3" />
              <p className="text-text-secondary">
                {searchQuery ? '검색 결과가 없습니다' : '분석 이력이 없습니다'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredAnalyses.map((analysis) => (
                <button
                  key={analysis.id}
                  onClick={() => handleSelect(analysis)}
                  className="p-4 border border-border-default rounded-lg hover:border-accent-gold hover:bg-bg-elevated transition-all text-left group"
                >
                  <div className="flex gap-4">
                    {/* Thumbnail */}
                    <div className="w-20 h-28 flex-shrink-0 bg-bg-elevated rounded overflow-hidden border border-border-subtle">
                      {analysis.source_image_url ? (
                        <img
                          src={analysis.source_image_url.startsWith('http')
                            ? analysis.source_image_url
                            : `${API_BASE}${analysis.source_image_url}`}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to placeholder on error
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      ) : null}
                      {(!analysis.source_image_url || !analysis.source_image_url.startsWith('http')) && (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-text-tertiary" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-medium text-text-primary group-hover:text-accent-gold transition-colors truncate">
                          {getAnalysisName(analysis)}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          analysis.status === 'completed'
                            ? 'bg-green-500/10 text-green-500'
                            : analysis.status === 'failed'
                            ? 'bg-red-500/10 text-red-500'
                            : 'bg-yellow-500/10 text-yellow-500'
                        }`}>
                          {analysis.status === 'completed' ? '완료' :
                           analysis.status === 'failed' ? '실패' : '처리중'}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-text-tertiary">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>
                            {formatRelativeTime(analysis.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5" />
                          <span className="font-mono">{analysis.id.slice(0, 12)}</span>
                        </div>
                      </div>

                      {/* Analysis summary */}
                      <div className="mt-2 flex gap-1.5 flex-wrap">
                        {analysis.background_analysis && (
                          <span className="text-xs px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded">
                            배경
                          </span>
                        )}
                        {analysis.character_analysis && (
                          <span className="text-xs px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded">
                            인물
                          </span>
                        )}
                        {analysis.typography_analysis && (
                          <span className="text-xs px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded">
                            제목
                          </span>
                        )}
                        {analysis.subtitle_analysis && (
                          <span className="text-xs px-2 py-0.5 bg-orange-500/10 text-orange-400 rounded">
                            부제
                          </span>
                        )}
                        {analysis.author_analysis && (
                          <span className="text-xs px-2 py-0.5 bg-pink-500/10 text-pink-400 rounded">
                            저자
                          </span>
                        )}
                        {analysis.publisher_analysis && (
                          <span className="text-xs px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded">
                            출판사
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-bg-primary border-t border-border-subtle p-4">
          <button
            onClick={onClose}
            className="btn btn-secondary w-full"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}
